import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  DialogActions,
  DialogContentText,
  ThemeProvider,
  createTheme,
  useTheme,
  useMediaQuery,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Email as EmailIcon,
  History as HistoryIcon,
  InfoOutlined as InfoOutlinedIcon,
  Close,
} from "@mui/icons-material";
import { UserPlus } from "lucide-react";
import { styled } from "@mui/material/styles";
import SendEmailModal from "../Emailing/SendEmailModal";
import EmailHistory from "../Emailing/EmailHistory";
import ApplicationSearchBar from "./ApplicationSearchBar";
import { OutlinedButton } from "../../components/Buttons/Buttons";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

// Default theme to ensure components have access to theme even if parent doesn't provide one
const defaultTheme = createTheme();

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  width: "100%",
  borderRadius: "24px",
  marginBottom: theme.spacing(4),
  overflow: "hidden",
  position: "relative",
  background: "#ffffff",
  border: "1px solid rgba(0, 23, 231, 0.06)",
}));

const ResponsiveAvatar = styled(Avatar)(({ theme }) => ({
  width: 90,
  height: 90,
  marginRight: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    width: 60,
    height: 60,
    marginRight: theme.spacing(1),
  },
  backgroundColor: "rgba(0, 23, 231, 0.08)",
  backdropFilter: "blur(4px)",
}));

// Helper functions
const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
  } catch (error) {
    console.error("Failed to parse JWT:", error);
    return {};
  }
};

const parseStringToArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    if (data.startsWith("[") && data.endsWith("]")) {
      try {
        return JSON.parse(data);
      } catch (e) {
        const cleanedString = data
          .replace(/\[|\]|'|"/g, "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
        return cleanedString;
      }
    }
    if (data.includes(",")) {
      return data
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [data];
  }
  return [];
};

const cleanString = (str) => {
  if (!str) return "";
  return str.replace(/^\[|\]$|'|"/g, "").trim();
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return dateString;
  }
};

const truncateLabel = (label, maxLength = 15) => {
  if (typeof label !== "string") return label;
  return label.length > maxLength
    ? label.slice(0, maxLength) + "..."
    : label;
};

const Blacklist = ({
  candidates: initialCandidates,
  post_title,
  onUnblock,
  onUnblockSuccess = null, // Callback after successful unblock
  hideSearchBar = false,
  location = "",
  onNavigateToApplicationTable = null, // Navigation callback to ApplicationTable
  onNavigateToDynamicForms = null, // Navigation callback to DynamicForms
  initialTotalCount = null, // Total count from infinite scroll
  enableInfiniteScroll = false, // Whether infinite scroll is enabled
  infiniteScrollPageSize = 20, // Page size for infinite scroll
  fetchMoreData = null, // Function to fetch more data for infinite scroll
  onSearchResults = null, // Function to handle search results for infinite scroll
  infiniteScrollData = null, // Data from infinite scroll component
  infiniteScrollTotalCount = null, // Total count from infinite scroll component
}) => {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [moreItemsDialogOpen, setMoreItemsDialogOpen] = useState(false);
  const [moreItemsDialogTitle, setMoreItemsDialogTitle] = useState("");
  const [moreItemsDialogContent, setMoreItemsDialogContent] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [candidateToUnblock, setCandidateToUnblock] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isEmailHistoryOpen, setIsEmailHistoryOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [scale, setScale] = useState(1);
  const initializationRef = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));


  // Search API endpoints for blacklist
  const SEARCH_API_ENDPOINT = `${apiUrl}candidates/api/blacklist/search/`;
  const AUTOCOMPLETE_API_ENDPOINT = `${apiUrl}candidates/api/blacklist/autocomplete/`;

  // Add scaling effect
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const contentHeight = 800;
      const contentWidth = 1200;

      const heightScale = (windowHeight * 0.95) / contentHeight;
      const widthScale = (windowWidth * 0.95) / contentWidth;
      let newScale = Math.min(heightScale, widthScale, 1);

      if (windowWidth <= 1920) {
        newScale = Math.max(newScale, 0.8);
      } else if (windowWidth <= 2560) {
        newScale = Math.max(newScale, 0.85);
      } else {
        newScale = Math.max(newScale, 0.9);
      }

      setScale(newScale);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Set initial candidates when component mounts or initialCandidates changes
  useEffect(() => {
    console.log("🔍 BLACKLIST INITIAL DEBUG - Setting candidates with dataType mapping:", initialCandidates);

    // If infinite scroll is enabled, don't manage local state - let the infinite scroll component handle it
    if (enableInfiniteScroll) {
      console.log("🔍 BLACKLIST INITIAL DEBUG - Infinite scroll enabled, skipping local state management");
      return;
    }

    // Prevent excessive re-initialization
    if (initializationRef.current && Array.isArray(initialCandidates) && initialCandidates.length === 0) {
      console.log("🔍 BLACKLIST INITIAL DEBUG - Skipping re-initialization with empty data");
      return;
    }
    initializationRef.current = true;

    if (Array.isArray(initialCandidates)) {
      setCandidates(initialCandidates);
      setTotalCount(initialCandidates.length);
    } else if (
      initialCandidates &&
      typeof initialCandidates === "object" &&
      initialCandidates.results
    ) {
      setCandidates(initialCandidates.results);
      setTotalCount(initialCandidates.count || initialCandidates.results.length);
    } else {
      setCandidates([]);
      setTotalCount(0);
    }
  }, [initialCandidates, enableInfiniteScroll, initialTotalCount]);

  // Debug: Log when candidates state changes
  useEffect(() => {
    console.log("🔍 BLACKLIST STATE DEBUG - candidates state changed:", candidates.slice(0, 2).map(c => ({ id: c.id, name: c.name || c.full_name })));
  }, [candidates]);

  // Search result handler
  const handleSearchResults = (results, totalCount = null, searchParams = {}) => {
    const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    console.log("🔍 BLACKLIST SEARCH DEBUG - handleSearchResults called with:", {
      results: Array.isArray(results) ? `Array(${results.length})` : results,
      totalCount: totalCount,
      searchParams: searchParams,
      enableInfiniteScroll: enableInfiniteScroll,
      onSearchResults: !!onSearchResults,
      currentScrollPosition: currentScrollPosition,
      timestamp: new Date().toISOString()
    });

    // If infinite scroll is enabled and onSearchResults is provided, delegate to it
    if (enableInfiniteScroll && onSearchResults) {
      console.log("🔍 BLACKLIST SEARCH DEBUG - Delegating to infinite scroll handler, current scroll:", currentScrollPosition);
      onSearchResults(results, totalCount, searchParams);
      return;
    } else {
      console.log("🔍 BLACKLIST SEARCH DEBUG - NOT delegating to infinite scroll:", {
        enableInfiniteScroll: enableInfiniteScroll,
        onSearchResults: !!onSearchResults,
        willUpdateLocalState: true
      });
    }

    // Handle null results (clear button clicked)
    if (results === null) {
      console.log("🔍 BLACKLIST SEARCH DEBUG - Clear button clicked, resetting...");
      if (enableInfiniteScroll && onSearchResults) {
        // For infinite scroll, delegate the reset to the parent
        onSearchResults(null, 0, {});
      } else {
        // For regular mode, reset local state
        setCandidates(initialCandidates || []);
        setTotalCount(initialCandidates?.length || 0);
      }
      return;
    }

    if (Array.isArray(results)) {
      console.log("🔍 BLACKLIST SEARCH DEBUG - Processing search results:", results.length, "items");

      const normalizedResults = results.map((item) => {
        const hasNestedCandidate = item.candidate && typeof item.candidate === "object";
        const candidateData = hasNestedCandidate ? item.candidate : item;
        const blacklistId = item.id;
        const candidateId = item.candidate_id || candidateData.id;

        return {
          ...candidateData,
          blacklist_id: blacklistId,
          candidate_id: candidateId,
          reason: item.reason || (hasNestedCandidate ? item.reason : candidateData.reason),
          date_blacklisted: item.date_blacklisted,
        };
      });

      // For infinite scroll mode, don't update local state directly
      // The parent infinite scroll component will handle the data updates
      if (!enableInfiniteScroll) {
        console.log("🔍 BLACKLIST SEARCH DEBUG - Setting normalized results:", normalizedResults.length, "candidates");
        setCandidates(normalizedResults);
        const newTotalCount = totalCount !== null ? totalCount : normalizedResults.length;
        setTotalCount(newTotalCount);
      }
    } else {
      console.log("🔍 BLACKLIST SEARCH DEBUG - Invalid search results, clearing data");
      if (!enableInfiniteScroll) {
        setCandidates([]);
        setTotalCount(0);
      }
    }
  };

  // Process blacklist data
  const processBlacklistData = (candidate) => {
    if (!candidate) {
      return {
        name: t("applicationTable.noName", "No Name"),
        firstName: "",
        lastName: "",
        email: t("applicationTable.noEmail", "No Email"),
        phone: t("applicationTable.noPhone", "No Phone"),
        profile: t("applicationTable.noProfileAvailable", "No profile available"),
        location: t("applicationTable.noLocationProvided", "No location provided"),
        city: "",
        address: "",
        skills: [],
        education: [],
        languages: [],
        certificates: [],
        linkedIn: "",
        experiences: [],
        reason: t("applicationTable.noReasonProvided", "No reason provided"),
        date_blacklisted: new Date().toISOString(),
        extracted_informations: { agexperiece: 0 }
      };
    }

    const candidateData = candidate.candidate || candidate;

    let experiences = [];
    let yearsOfExperience = 0;
    let languages = [];
    let certificates = [];

    if (candidateData.experiences) {
      try {
        const expData =
          typeof candidateData.experiences === "string"
            ? JSON.parse(candidateData.experiences)
            : candidateData.experiences;

        experiences = expData.EXPERIENCE || [];
        yearsOfExperience = expData.agexperiece || 0;
        languages = expData.Language || [];
        certificates = expData.Certificates || [];
      } catch (error) {
        console.error("Error parsing experiences:", error);
        experiences = [];
      }
    }

    const skills = parseStringToArray(candidateData.skills || []);
    const education = parseStringToArray(candidateData.educations || []);

    let profile = t("applicationTable.noProfileAvailable", "No profile available");
    if (candidateData.profile) {
      try {
        const profileArray = JSON.parse(candidateData.profile);
        profile =
          profileArray[0] ||
          t("applicationTable.noProfileAvailable", "No profile available");
      } catch (error) {
        profile = cleanString(candidateData.profile);
      }
    }

    return {
      name:
        `${candidateData.first_name || ""} ${candidateData.last_name || ""}`.trim() ||
        t("applicationTable.noName", "No Name"),
      firstName: candidateData.first_name || "",
      lastName: candidateData.last_name || "",
      first_name: candidateData.first_name || "",
      last_name: candidateData.last_name || "",
      email: candidateData.email || t("applicationTable.noEmail", "No Email"),
      phone: candidateData.phone || t("applicationTable.noPhone", "No Phone"),
      profile: profile,
      location:
        candidateData.city ||
        t("applicationTable.noLocationProvided", "No location provided"),
      city: candidateData.city || "",
      address: candidateData.address || "",
      skills: skills,
      education: education,
      languages: languages,
      certificates: certificates,
      linkedIn: candidateData.linkedin_profile || "",
      experiences: experiences,
      reason:
        candidate.reason ||
        t("applicationTable.noReasonProvided", "No reason provided"),
      date_blacklisted: candidate.date_blacklisted || new Date().toISOString(),
      extracted_informations: {
        agexperiece: yearsOfExperience,
      },
    };
  };

  const getUserIdFromAccessToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return null;
    try {
      return parseJwt(accessToken).user_id;
    } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
    }
  };

  const handleEmailClick = (candidateRaw) => {
    const candidate = processBlacklistData(candidateRaw);
    setSelectedCandidate({
      ...candidateRaw,
      ...candidate,
      email: candidate.email
    });
    setIsTemplateModalOpen(true);
  };

  const handleEmailHistoryClick = (candidateRaw) => {
    const candidate = processBlacklistData(candidateRaw);
    setSelectedCandidate({
      ...candidateRaw,
      ...candidate,
      email: candidate.email
    });
    setIsEmailHistoryOpen(true);
  };

  const handleShowMore = (title, items) => {
    setMoreItemsDialogTitle(title);
    const processedContent = items.map((item, index) => {
      if (typeof item === "string" && item.includes("LinkedIn:")) {
        const linkedInUrl = item.replace("LinkedIn:", "").trim();
        return {
          type: "link",
          label: t("applicationTable.seeLinkedInProfile", "LinkedIn Profile"),
          href: linkedInUrl.startsWith("http")
            ? linkedInUrl
            : `https://www.linkedin.com/in/${linkedInUrl}`,
        };
      }
      if (typeof item === "object" && item !== null) {
        if (item.type === "link") {
          return item;
        }
        const text =
          item.position ||
          item.text ||
          item.label ||
          item.name ||
          JSON.stringify(item);
        return String(text);
      }
      return String(item);
    });
    
    setMoreItemsDialogContent(processedContent);
    setMoreItemsDialogOpen(true);
  };

  const confirmUnblock = (candidateData, candidateName) => {
    let candidateId = candidateData.candidate_id;
    if (candidateId === undefined) {
      candidateId = candidateData.id;
    }
    setCandidateToUnblock({ id: candidateId, name: candidateName });
    setConfirmDialogOpen(true);
  };

  const handleUnblock = async () => {
    if (!candidateToUnblock) return;
    const candidateId = candidateToUnblock.id;

    setActionLoading((prev) => ({ ...prev, [candidateId]: true }));

    try {
      if (onUnblock) {
        await onUnblock(candidateId);
      }
      // Call success callback after successful unblock
      if (onUnblockSuccess) {
        onUnblockSuccess();
      }
    } catch (error) {
      console.error("Unblock failed:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [candidateId]: false }));
      setConfirmDialogOpen(false);
      setCandidateToUnblock(null);
    }
  };

  // Determine the data source: use infinite scroll data if available, otherwise use local state
  const candidatesToRender = enableInfiniteScroll && infiniteScrollData ? infiniteScrollData : candidates;
  const totalCountToShow = enableInfiniteScroll && infiniteScrollTotalCount !== null ? infiniteScrollTotalCount : totalCount;

  console.log("🔍 BLACKLIST DATA SOURCE DEBUG:", {
    enableInfiniteScroll: enableInfiniteScroll,
    infiniteScrollDataLength: infiniteScrollData ? infiniteScrollData.length : 0,
    localCandidatesLength: candidates.length,
    usingInfiniteScrollData: enableInfiniteScroll && infiniteScrollData,
    candidatesToRenderLength: candidatesToRender.length,
    localTotalCount: totalCount,
    infiniteScrollTotalCount: infiniteScrollTotalCount,
    totalCountToShow: totalCountToShow
  });

  return (
    <Box
      sx={{
        zoom: scale,
        transformOrigin: "top left",
        transition: "zoom 0.3s ease-in-out",
      }}
    >
      {/* Search bar */}
      {!hideSearchBar && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <ApplicationSearchBar
              data={candidatesToRender}
              onSearch={handleSearchResults}
              SEARCH_API_ENDPOINT={SEARCH_API_ENDPOINT}
              AUTOCOMPLETE_API_ENDPOINT={AUTOCOMPLETE_API_ENDPOINT}
              dataType="blacklist"
              location="location"
              resultCount={totalCountToShow}
              onNavigateToBlacklist={onNavigateToApplicationTable}
              onNavigateToDynamicForms={onNavigateToDynamicForms}
            />
          </Box>
        </Box>
      )}

      {/* Display message if no candidates are found */}
      {candidatesToRender.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            backgroundColor: "#f5f5f5",
            borderRadius: 2,
            my: 3,
          }}
        >
          <Typography
            variant="h6"
            color="textSecondary"
            sx={{ fontSize: "1.3rem" }}
          >
            {t(
              "applicationTable.noCandidatesFound",
              "No candidates found matching your search criteria"
            )}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mt: 1, fontSize: "1.1rem" }}
          >
            {t(
              "applicationTable.tryAdjustingFilters",
              "Try adjusting your search filters or clear them to see all candidates"
            )}
          </Typography>
        </Box>
      )}

      {/* Blacklisted candidates - 2 cards per row */}
      <Grid container spacing={3}>
        {candidatesToRender
          .filter(candidateRaw => candidateRaw != null)
          .map((candidateRaw, index) => {
            const candidate = processBlacklistData(candidateRaw);

            // Generate stable key using candidate ID or fallback to index
            const stableKey = candidateRaw.id || candidateRaw.candidate_id || candidateRaw.blacklist_id || `blacklist-${index}`;

            // Debug logging for first few items
            if (index < 3) {
              console.log("🔍 BLACKLIST RENDER DEBUG - Candidate", index, ":", {
                id: candidateRaw.id,
                candidate_id: candidateRaw.candidate_id,
                blacklist_id: candidateRaw.blacklist_id,
                stableKey: stableKey,
                name: candidate.name,
                totalRendered: candidatesToRender.length
              });
            }

            return (
              <Grid item xs={12} md={6} key={stableKey}>
                <StyledCard>
                  <CardContent
                    className="p-4"
                    sx={{
                      pt: { xs: "48px", sm: "48px", md: "64px" },
                      pb: { xs: 3, sm: 4, md: 5 },
                      px: { xs: 3, sm: "48px", md: "48px" },
                    }}
                  >
                    <Grid container spacing={2} alignItems="flex-start">
                      {/* Column 1: Name, Profile, Contact Info */}
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <Box className="flex flex-col gap-1 mb-1">
                          {/* Name and Image Section */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              mb: 2,
                              gap: 2,
                            }}
                          >
                            {/* Avatar/Image */}
                            <Avatar
                              src={
                                candidate.image &&
                                candidate.image !== "null" &&
                                candidate.image !== null &&
                                candidate.image.startsWith("http")
                                  ? candidate.image
                                  : candidate.image &&
                                    candidate.image !== "null" &&
                                    candidate.image !== null
                                  ? `${apiUrl}${candidate.image}`
                                  : undefined
                              }
                              alt={`${candidate.firstName} ${candidate.lastName}`}
                              sx={{
                                width: 60,
                                height: 60,
                                border: "2px solid #e0e0e0",
                                backgroundColor: "#e0e0e0",
                                color: "#666",
                                fontSize: "1.2rem",
                                fontWeight: 500,
                              }}
                            >
                              {candidate.firstName?.charAt(0)}
                              {candidate.lastName?.charAt(0)}
                            </Avatar>

                            {/* Name and Applied Date */}
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{ fontSize: "1.5rem", mb: 0.25 }}
                              >
                                {candidate.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.9rem",
                                  fontWeight: 500,
                                  display: "block",
                                  mt: 0.25,
                                }}
                              >
                                <Box component="span" sx={{ color: "#0017E7" }}>
                                  {t("applicationTable.blacklisted", "Blacklisted")}:
                                </Box>
                                <Box
                                  component="span"
                                  sx={{ color: "#000000", ml: 0.5 }}
                                >
                                  {formatDate(candidate.date_blacklisted)}
                                </Box>
                              </Typography>
                            </Box>
                          </Box>

                          {/* Profile and Contact Info Section */}
                          <Box
                            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                          >
                            {/* Profile */}
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.9rem",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                lineHeight: "1.4",
                                maxWidth: "100%",
                                color: "#000000",
                                fontWeight: 500,
                              }}
                            >
                              {candidate.profile ||
                                t(
                                  "applicationTable.noProfileAvailable",
                                  "No profile available"
                                )}
                            </Typography>

                            {/* Contact Info */}
                            {(() => {
                              const contactInfo = [
                                candidate.email && {
                                  type: "email",
                                  icon: <EmailIcon />,
                                  label: candidate.email,
                                },
                                candidate.phone && {
                                  type: "phone",
                                  icon: <EmailIcon />,
                                  label: candidate.phone,
                                },
                                candidate.linkedIn && {
                                  type: "linkedin",
                                  icon: <EmailIcon />,
                                  label: t(
                                    "applicationTable.seeLinkedInProfile",
                                    "Voir le profil LinkedIn"
                                  ),
                                  href: candidate.linkedIn.startsWith("http")
                                    ? candidate.linkedIn
                                    : `https://www.linkedin.com/in/${candidate.linkedIn}`,
                                },
                              ].filter(Boolean);

                              if (contactInfo.length === 0) return null;

                              return (
                                <Box className="flex flex-wrap gap-2">
                                  <Button
                                    variant="text"
                                    color="primary"
                                    onClick={() => {
                                      const mappedContactInfo = contactInfo.map((c) => {
                                        if (c.type === "linkedin") {
                                          return {
                                            type: "link",
                                            label: c.label,
                                            href: c.href,
                                          };
                                        }
                                        return c.label;
                                      });
                                      handleShowMore("Contact Information", mappedContactInfo);
                                    }}
                                    size="small"
                                    sx={{
                                      textTransform: "none !important",
                                      color: "#0017E7",
                                      fontWeight: 500,
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {t(
                                      "applicationTable.contactInformation",
                                      "Contact Information"
                                    )}
                                  </Button>
                                </Box>
                              );
                            })()}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Column 2: Reason for Block */}
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <Box
                          className="flex flex-col gap-3 mb-1"
                          sx={{
                            height: "100%",
                            justifyContent: "flex-end",
                          }}
                        >
                          {/* Reason for Block */}
                          <Box>
                            <Typography
                              variant="h6"
                              className="mb-1 font-semibold flex items-center"
                              fontWeight="bold"
                              sx={{ fontSize: "1.1rem" }}
                            >
                              <InfoOutlinedIcon className="w-6 h-6" sx={{ mr: 1 }} />
                              {t("applicationTable.reasonForBlock", "Reason for Block")}
                            </Typography>
                            {candidate.reason && candidate.reason.trim() !== "" ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 1,
                                }}
                              >
                                <Chip
                                  label={truncateLabel(candidate.reason, 15)}
                                  className="mr-3"
                                  sx={{
                                    fontSize: "1rem",
                                    backgroundColor: "rgba(0, 23, 231, 0.08)",
                                    color: "#0017E7",
                                    border: "1px solid rgba(0, 23, 231, 0.15)",
                                    fontWeight: 500,
                                    "& .MuiChip-label": {
                                      fontSize: "1rem",
                                      fontWeight: 500,
                                    },
                                  }}
                                />
                                {candidate.reason.length > 15 && (
                                  <Button
                                    variant="text"
                                    color="primary"
                                    onClick={() =>
                                      handleShowMore("Reason for Block", [candidate.reason])
                                    }
                                    size="small"
                                    sx={{
                                      textTransform: "none !important",
                                      color: "#0017E7",
                                      fontWeight: 500,
                                      fontSize: "0.875rem",
                                      "&:hover": {
                                        backgroundColor: "transparent",
                                        color: "#0017E7",
                                      },
                                    }}
                                  >
                                    {t("applicationTable.seeMore", "See More")}
                                  </Button>
                                )}
                              </Box>
                            ) : (
                              <Chip
                                label={t(
                                  "applicationTable.noReasonProvided",
                                  "No reason provided"
                                )}
                                sx={{
                                  fontSize: "1rem",
                                  "& .MuiChip-label": { fontSize: "1rem" },
                                  backgroundColor: "rgba(0, 23, 231, 0.08)",
                                  color: "#0017E7",
                                  border: "1px solid rgba(0, 23, 231, 0.15)",
                                  fontWeight: 500,
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Actions Section */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1,
                        width: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        pt: 2,
                        borderTop: "1px solid rgba(0, 23, 231, 0.1)",
                        mt: 2,
                      }}
                    >
                      <OutlinedButton
                        startIcon={<UserPlus size={18} />}
                        onClick={() => confirmUnblock(candidateRaw, candidate.name)}
                        disabled={actionLoading[candidateRaw.id]}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          flex: 1,
                          height: "auto",
                          py: 0.5,
                        }}
                      >
                        {actionLoading[candidateRaw.id] ? (
                          <CircularProgress size={24} />
                        ) : (
                          t("applicationTable.unblock", "UNBLOCK")
                        )}
                      </OutlinedButton>
                      
                      <OutlinedButton
                        startIcon={<HistoryIcon />}
                        onClick={() => handleEmailHistoryClick(candidateRaw)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          flex: 1,
                          height: "auto",
                          py: 0.5,
                        }}
                      >
                        {t("applicationTable.emailHistory", "EMAIL HISTORY")}
                      </OutlinedButton>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            );
          })}
      </Grid>


      {/* "Show More" Dialog */}
      <Dialog
        open={moreItemsDialogOpen}
        onClose={() => setMoreItemsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: "rgba(0, 23, 231, 0.03)",
            borderBottom: "1px solid rgba(0, 23, 231, 0.1)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: 600,
              color: "#0017E7",
              fontSize: "1.2rem",
            }}
          >
            {t(
              `applicationTable.dialogTitles.${moreItemsDialogTitle
                .toLowerCase()
                .replace(/\s+/g, "")}`,
              moreItemsDialogTitle
            )}
          </Typography>
          <IconButton
            aria-label={t("applicationTable.close", "close")}
            onClick={() => setMoreItemsDialogOpen(false)}
            sx={{
              color: "rgba(0, 23, 231, 0.6)",
              "&:hover": {
                color: "#0017E7",
                backgroundColor: "rgba(0, 23, 231, 0.08)",
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 0.5, sm: 0.75, md: 1 }, p: { xs: 1.5, sm: 2, md: 2 } }}>
            {moreItemsDialogContent.map((item, i) => {
              // Handle LinkedIn links
              if (typeof item === 'string' && item.includes('linkedin.com')) {
                return (
                  <Chip
                    key={i}
                    label="Voir le profil LinkedIn"
                    component="a"
                    href={item}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    sx={{
                      margin: { xs: "2px", sm: "3px", md: "4px" },
                      backgroundColor: "#1976d2 !important",
                      color: "#ffffff !important",
                      border: "2px solid #1976d2",
                      fontWeight: 600,
                      minHeight: { xs: "32px", sm: "36px", md: "40px" },
                      cursor: "pointer",
                      textDecoration: "none",
                      "&:hover": {
                        backgroundColor: "#1565c0 !important",
                        transform: "scale(1.05)",
                        boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
                      },
                      "& .MuiChip-label": {
                        padding: { xs: "8px 12px", sm: "10px 16px", md: "12px 20px" },
                        fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                        fontWeight: 600,
                      },
                    }}
                  />
                );
              }
              
              // Handle link objects
              if (typeof item === "object" && item !== null && item.type === "link") {
                return (
                  <Chip
                    key={i}
                    label={item.label || "Link"}
                    component="a"
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    sx={{
                      margin: { xs: "2px", sm: "3px", md: "4px" },
                      backgroundColor: "#1976d2 !important",
                      color: "#ffffff !important",
                      border: "2px solid #1976d2",
                      fontWeight: 600,
                      minHeight: { xs: "32px", sm: "36px", md: "40px" },
                      cursor: "pointer",
                      textDecoration: "none",
                      "&:hover": {
                        backgroundColor: "#1565c0 !important",
                        transform: "scale(1.05)",
                        boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
                      },
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        padding: { xs: "8px 12px", sm: "10px 16px", md: "12px 20px" },
                        fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                        lineHeight: { xs: "1.3", sm: "1.4", md: "1.5" },
                        fontWeight: 600,
                      },
                    }}
                  />
                );
              }
              
              // Handle regular items
              return (
                <Chip
                  key={i}
                  label={String(item)}
                  sx={{
                    margin: { xs: "2px", sm: "3px", md: "4px" },
                    backgroundColor: "rgba(0, 23, 231, 0.08)",
                    color: "#0017E7",
                    border: "1px solid rgba(0, 23, 231, 0.15)",
                    fontWeight: 500,
                    minHeight: { xs: "28px", sm: "32px", md: "36px" },
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      padding: { xs: "6px 8px", sm: "8px 12px", md: "12px 16px" },
                      fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                      lineHeight: { xs: "1.3", sm: "1.4", md: "1.5" },
                    },
                  }}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: 2,
            borderTop: "1px solid rgba(0, 23, 231, 0.1)",
            backgroundColor: "rgba(0, 23, 231, 0.02)",
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => setMoreItemsDialogOpen(false)}
            color="primary"
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 500,
              backgroundColor: "#0017E7",
              "&:hover": {
                backgroundColor: "rgba(0, 23, 231, 0.9)",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0, 23, 231, 0.25)",
              },
            }}
          >
            {t("applicationTable.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Unblock Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle sx={{ fontSize: "1.3rem" }}>
          {t("applicationTable.confirmUnblock", "Confirm Unblock")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "1.1rem" }}>
            {t(
              "applicationTable.removeFromBlacklist",
              "Remove {{name}} from blacklist?",
              { name: candidateToUnblock?.name }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            sx={{ fontSize: "1.1rem" }}
          >
            {t("applicationTable.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleUnblock}
            color="primary"
            variant="contained"
            disabled={actionLoading[candidateToUnblock?.id]}
            sx={{ fontSize: "1.1rem" }}
          >
            {actionLoading[candidateToUnblock?.id] ? (
              <CircularProgress size={24} />
            ) : (
              t("applicationTable.confirm", "Confirm")
            )}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Email Modals */}
      {selectedCandidate && (
        <ThemeProvider theme={defaultTheme}>
          {isTemplateModalOpen && (() => {
            const candidateInfoData = {
              email: selectedCandidate.email,
              firstName: selectedCandidate.first_name || "",
              lastName: selectedCandidate.last_name || "",
              company: post_title,
              position: post_title,
            };
            return (
              <SendEmailModal
                open={isTemplateModalOpen}
                onClose={() => setIsTemplateModalOpen(false)}
                recruiterId={getUserIdFromAccessToken()}
                theme={defaultTheme}
                candidateInfo={candidateInfoData}
                showTemplateSelection={true}
              />
            );
          })()}

          {isEmailHistoryOpen && (
            <EmailHistory
              open={isEmailHistoryOpen}
              onClose={() => setIsEmailHistoryOpen(false)}
              candidateEmail={selectedCandidate.email}
              recruiterId={getUserIdFromAccessToken()}
              theme={defaultTheme}
            />
          )}
        </ThemeProvider>
      )}
    </Box>
  );
};

export default React.memo(Blacklist);